import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import {
  loops,
  models as modelsApi,
  projects,
  security,
  type LoopProjectStatus,
  type LoopStatus,
  type LoopRun,
} from "../../lib/api";
import type { TriageStatus } from "../../lib/types";
import { useUI } from "../../lib/store";
import { useWebSocket } from "../../hooks/useWebSocket";
import { ModelPicker } from "../shared/ModelPicker";
import { Skeleton } from "../shared/Skeleton";
import { cn } from "../../lib/cn";
import { formatClock, formatTokens } from "../../lib/format";

/**
 * Loops — scoped to the selected project (like Activity / Chat): its loop
 * switches, counts, token budget, on-demand triage, and escalations. With
 * "All projects" selected it falls back to a compact overview of every project.
 * The global on/off lives in the account menu (with models); here we
 * only show a read-only line of its state. A /ws/loops nudge refetches the
 * instant a tick lands; a 30s poll is the fallback.
 */
export function LoopActivityPanel() {
  const qc = useQueryClient();
  const selectedProjectId = useUI((s) => s.selectedProjectId);

  const onLoopEvent = useCallback(
    (msg: { type?: string }) => {
      if (msg?.type === "loops") {
        qc.invalidateQueries({ queryKey: ["loops", "status"] });
        qc.invalidateQueries({ queryKey: ["loops", "runs"] });
      }
    },
    [qc],
  );
  useWebSocket<{ type?: string }>({ url: "/ws/loops", onMessage: onLoopEvent });

  const { data: status, isLoading } = useQuery({
    queryKey: ["loops", "status"],
    queryFn: loops.status,
    refetchInterval: 30_000,
  });
  const { data: runs } = useQuery({
    queryKey: ["loops", "runs", selectedProjectId ?? "all"],
    queryFn: () =>
      loops.runs({
        limit: 40,
        project_id: selectedProjectId ?? undefined,
        nonempty: true,
      }),
    refetchInterval: 30_000,
  });

  if (isLoading) {
    return (
      <div className="px-3 py-3 space-y-2">
        <Skeleton height={20} />
        <Skeleton height={48} />
        <Skeleton height={48} />
      </div>
    );
  }

  const all = status?.projects ?? [];
  const scoped = selectedProjectId
    ? all.find((p) => p.project_id === selectedProjectId)
    : null;

  return (
    <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
      <GlobalStatusLine status={status} />

      {!selectedProjectId && (
        <div className="space-y-1.5">
          <div className="text-caption text-text-tertiary uppercase tracking-wide px-0.5">
            Projects
          </div>
          {all.map((p) => (
            <ProjectRow key={p.project_id} p={p} />
          ))}
          {all.length === 0 && (
            <div className="text-small text-text-tertiary px-0.5">No projects.</div>
          )}
        </div>
      )}

      {selectedProjectId &&
        (scoped ? (
          <ProjectLoopControls p={scoped} />
        ) : (
          <div className="text-small text-text-tertiary px-0.5">
            No loop config for this project yet.
          </div>
        ))}

      <div className="space-y-1.5">
        <div className="text-caption text-text-tertiary uppercase tracking-wide px-0.5">
          Recent loop runs
        </div>
        {(runs ?? []).map((r) => (
          <RunRow key={r.id} run={r} />
        ))}
        {(runs ?? []).length === 0 && (
          <div className="text-small text-text-tertiary px-0.5">
            No loop activity yet.
          </div>
        )}
      </div>
    </div>
  );
}

/** Read-only line — the global on/off itself lives in the account menu. */
function GlobalStatusLine({ status }: { status: LoopStatus | undefined }) {
  const env = status?.env_enabled ?? false;
  const on = status?.master_enabled ?? false;
  const color = on ? "var(--status-success)" : "var(--text-quaternary)";
  const text = on
    ? "Global loops on"
    : env
      ? "Global loops off — turn on in the account menu"
      : "Global loops off (env ceiling) — set AUTONOMOUS_LOOPS_ENABLED";
  return (
    <div className="flex items-center gap-2 rounded-md bg-bg-inset px-3 py-2">
      <span
        className="inline-block h-2 w-2 rounded-full shrink-0"
        style={{ background: color }}
      />
      <span className="text-mono text-[11px] text-text-tertiary leading-snug">
        {text}
      </span>
    </div>
  );
}

/** Reusable project-loop patch mutation, optimistic on the status cache. */
function useProjectLoopPatch(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: {
      manager_loop_enabled?: boolean;
      worker_loop_enabled?: boolean;
      security_scan_enabled?: boolean;
      triage_requires_confirmation?: boolean;
      worker_model?: string | null;
    }) => projects.update(projectId, patch),
    onMutate: async (patch) => {
      await qc.cancelQueries({ queryKey: ["loops", "status"] });
      const prev = qc.getQueryData<LoopStatus>(["loops", "status"]);
      if (prev) {
        qc.setQueryData<LoopStatus>(["loops", "status"], {
          ...prev,
          projects: prev.projects.map((row) =>
            row.project_id === projectId ? { ...row, ...patch } : row,
          ),
        });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(["loops", "status"], ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["loops", "status"] });
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["project", projectId] });
    },
  });
}

/** Rich per-project control surface (the project-scoped Loops view). */
function ProjectLoopControls({ p }: { p: LoopProjectStatus }) {
  const patch = useProjectLoopPatch(p.project_id);
  return (
    <div className="space-y-3">
      <div className="rounded-md bg-bg-surface border border-border-subtle px-3 py-2 space-y-0.5">
        <SwitchRow
          label="Manager loop"
          checked={p.manager_loop_enabled}
          disabled={patch.isPending}
          onChange={(v) => patch.mutate({ manager_loop_enabled: v })}
        />
        <div className="h-px bg-border-subtle" />
        <SwitchRow
          label="Worker loop"
          checked={p.worker_loop_enabled}
          disabled={patch.isPending}
          onChange={(v) => patch.mutate({ worker_loop_enabled: v })}
        />
        <div className="h-px bg-border-subtle" />
        <SwitchRow
          label="Security scan"
          checked={p.security_scan_enabled}
          disabled={patch.isPending}
          onChange={(v) => patch.mutate({ security_scan_enabled: v })}
        />
        <div className="h-px bg-border-subtle" />
        <SwitchRow
          label="Shadow mode"
          checked={p.triage_requires_confirmation}
          disabled={patch.isPending}
          onChange={(v) => patch.mutate({ triage_requires_confirmation: v })}
        />
      </div>

      <WorkerModelControl projectId={p.project_id} patch={patch} />

      <CountsRow p={p} />
      <TokenBudgetBar p={p} />
      <TriageNowButton projectId={p.project_id} />
      <ScanNowButton projectId={p.project_id} />
    </div>
  );
}

/** Worker-loop model + effort picker (Loops menu). Patches project.worker_model. */
function WorkerModelControl({
  projectId,
  patch,
}: {
  projectId: string;
  patch: ReturnType<typeof useProjectLoopPatch>;
}) {
  const project = useQuery({
    queryKey: ["project", projectId],
    queryFn: () => projects.get(projectId),
    staleTime: 30_000,
  });
  const catalog = useQuery({
    queryKey: ["models"],
    queryFn: () => modelsApi.list(),
    staleTime: 5 * 60_000,
  });
  return (
    <div className="rounded-md bg-bg-surface border border-border-subtle px-3 py-2 space-y-1.5">
      <span className="text-small text-text-primary leading-tight">Worker model</span>
      <div className="grid grid-cols-2 gap-2">
        <ModelPicker
          value={project.data?.worker_model ?? ""}
          onChange={(v) => patch.mutate({ worker_model: v || null })}
          models={catalog.data?.models ?? []}
          crossProvider={false}
          defaultProvider={project.data?.cli_binary}
          modelPlaceholder="cheaper worker default"
          disabled={patch.isPending || !project.data}
        />
      </div>
    </div>
  );
}

/** One stacked switch row (label + Switch), used in the scoped view. */
function SwitchRow({
  label,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="text-small text-text-primary leading-tight min-w-0">{label}</span>
      <Switch
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        label={label}
      />
    </div>
  );
}

/** Clickable triage counts — filter the board to that state (and select it). */
function CountsRow({ p }: { p: LoopProjectStatus }) {
  const setProject = useUI((s) => s.setSelectedProjectId);
  const setFilter = useUI((s) => s.setTriageFilter);
  const pick = (s: TriageStatus) => {
    setProject(p.project_id);
    setFilter(s);
  };
  return (
    <div className="flex items-center gap-2 flex-wrap text-mono text-[11px] px-0.5">
      <span className="text-text-tertiary" title="Loop tasks running">
        ▶ {p.running}/{p.max_concurrent_auto}
      </span>
      <CountChip label="ready" value={p.agent_ready} onClick={() => pick("agent_ready")} />
      <CountChip
        label="human"
        value={p.needs_human}
        warn={p.needs_human > 0}
        onClick={() => pick("needs_human")}
      />
      <CountChip label="untriaged" value={p.untriaged} onClick={() => pick("untriaged")} />
    </div>
  );
}

function CountChip({
  label,
  value,
  warn,
  onClick,
}: {
  label: string;
  value: number;
  warn?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1 px-1.5 h-[18px] rounded-sm",
        "hover:bg-bg-hover transition-colors duration-fast ease-out",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
      )}
      style={{
        background: "var(--bg-inset)",
        color: warn ? "var(--status-paused)" : "var(--text-tertiary)",
      }}
      title={`Filter the board to ${label}`}
    >
      {label} {value}
    </button>
  );
}

/** Spend vs the rolling 5h auto-token budget (only when a budget is set). */
function TokenBudgetBar({ p }: { p: LoopProjectStatus }) {
  if (!p.token_window_budget) return null;
  const pct = Math.min(
    100,
    Math.round((p.token_window_spent / p.token_window_budget) * 100),
  );
  const color =
    pct >= 100
      ? "var(--status-error)"
      : pct >= 80
        ? "var(--status-warning)"
        : "var(--status-success)";
  return (
    <div className="space-y-1 px-0.5">
      <div className="flex items-baseline justify-between text-mono text-[11px]">
        <span className="text-text-tertiary">Auto token budget · 5h</span>
        <span className="text-text-secondary tabular-nums">
          {formatTokens(p.token_window_spent)} / {formatTokens(p.token_window_budget)}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-bg-inset overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
    </div>
  );
}

/** Run a manager triage pass over this project's backlog on demand. */
function TriageNowButton({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const run = useMutation({
    mutationFn: () => loops.triageNow(projectId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["loops", "status"] });
      qc.invalidateQueries({ queryKey: ["loops", "runs"] });
      qc.invalidateQueries({ queryKey: ["kanban"] });
    },
  });
  const result = run.data;
  return (
    <div className="px-0.5">
      <button
        type="button"
        onClick={() => run.mutate()}
        disabled={run.isPending}
        className={cn(
          "inline-flex items-center h-7 px-2.5 rounded-md text-small",
          "bg-bg-inset text-text-secondary border border-border-default",
          "hover:bg-bg-hover hover:text-text-primary transition-colors duration-fast ease-out",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
          run.isPending && "opacity-60 cursor-wait",
        )}
        title="Classify and route this project's untriaged backlog now"
      >
        {run.isPending ? "Triaging…" : "Triage now"}
      </button>
      {run.isError && (
        <span className="ml-2 text-mono text-[11px] text-[color:var(--status-error)]">
          {(run.error as Error).message}
        </span>
      )}
      {result && !run.isPending && (
        <span className="ml-2 text-mono text-[11px] text-text-tertiary">
          {result.acted} triaged / {result.examined} examined
        </span>
      )}
    </div>
  );
}

/** Run Trivy on this project's host now (one scan at a time). */
function ScanNowButton({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const { data: summary } = useQuery({
    queryKey: ["security", "summary"],
    queryFn: security.summary,
  });
  const host = summary?.hosts.find((h) => h.projects.some((p) => p.project_id === projectId))?.host;
  const run = useMutation({
    mutationFn: () => security.scanNow(host!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["security"] });
      qc.invalidateQueries({ queryKey: ["loops", "runs"] });
    },
  });
  return (
    <div className="px-0.5">
      <button
        type="button"
        onClick={() => run.mutate()}
        disabled={run.isPending || !host}
        className={cn(
          "inline-flex items-center h-7 px-2.5 rounded-md text-small",
          "bg-bg-inset text-text-secondary border border-border-default",
          "hover:bg-bg-hover hover:text-text-primary transition-colors duration-fast ease-out",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
          run.isPending && "opacity-60 cursor-wait",
        )}
        title={`Scan ${host ?? "this project's host"} for known vulnerabilities now`}
      >
        Scan now
      </button>
      {run.isError && (
        <span className="ml-2 text-mono text-[11px] text-[color:var(--status-error)]">
          {(run.error as Error).message}
        </span>
      )}
      {run.isSuccess && !run.isPending && (
        <span className="ml-2 text-mono text-[11px] text-text-tertiary">
          scan started · see the Security tab
        </span>
      )}
    </div>
  );
}

/** Compact project row for the All-projects overview. */
function ProjectRow({ p }: { p: LoopProjectStatus }) {
  const patch = useProjectLoopPatch(p.project_id);
  return (
    <div className="rounded-md bg-bg-surface border border-border-subtle px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-small text-text-primary truncate">{p.name}</span>
        <div className="flex items-center gap-1.5 shrink-0">
          <LoopTag
            on={p.manager_loop_enabled}
            label="mgr"
            title="Manager (triage) loop"
            disabled={patch.isPending}
            onToggle={() => patch.mutate({ manager_loop_enabled: !p.manager_loop_enabled })}
          />
          <LoopTag
            on={p.worker_loop_enabled}
            label="wrk"
            title="Worker (execute) loop"
            disabled={patch.isPending}
            onToggle={() => patch.mutate({ worker_loop_enabled: !p.worker_loop_enabled })}
          />
          <LoopTag
            on={p.security_scan_enabled}
            label="sec"
            title="Security scan"
            disabled={patch.isPending}
            onToggle={() => patch.mutate({ security_scan_enabled: !p.security_scan_enabled })}
          />
          {p.triage_requires_confirmation && (
            <span
              className="text-mono text-[10px] px-1 h-[16px] inline-flex items-center rounded-sm text-text-tertiary"
              style={{ background: "var(--bg-inset)" }}
              title="Shadow mode — worker only claims human-confirmed tasks"
            >
              shadow
            </span>
          )}
        </div>
      </div>
      <div className="mt-1">
        <CountsRow p={p} />
      </div>
    </div>
  );
}

function LoopTag({
  on,
  label,
  title,
  disabled,
  onToggle,
}: {
  on: boolean;
  label: string;
  title: string;
  disabled?: boolean;
  onToggle?: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={title}
      disabled={disabled}
      onClick={onToggle}
      className={cn(
        "text-mono text-[10px] px-1 h-[16px] inline-flex items-center rounded-sm",
        "transition-colors duration-fast ease-out",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
        disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
      )}
      style={
        on
          ? { color: "var(--status-running)", background: "var(--status-running-soft)" }
          : { color: "var(--text-quaternary)", background: "var(--bg-inset)" }
      }
      title={`${title}: ${on ? "on" : "off"} — click to toggle`}
    >
      {label}
    </button>
  );
}

/**
 * Small pill switch — on-brand, label-only via aria-label (no redundant text).
 * Accent fill when on, inset track when off; disabled dims and blocks clicks.
 */
function Switch({
  checked,
  disabled,
  onChange,
  label,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={cn(
        "relative inline-flex h-[18px] w-8 shrink-0 items-center rounded-full",
        "transition-colors duration-fast ease-out",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1 focus-visible:ring-offset-bg-canvas",
        disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer",
      )}
      style={{ background: checked ? "var(--accent)" : "var(--bg-surface)" }}
    >
      <span
        className="inline-block h-3 w-3 rounded-full bg-white transition-transform duration-fast ease-out"
        style={{ transform: checked ? "translateX(17px)" : "translateX(3px)" }}
      />
    </button>
  );
}

function RunRow({ run }: { run: LoopRun }) {
  return (
    <div className="flex items-center gap-2 rounded-md bg-bg-inset px-2.5 py-1.5 text-mono text-[11px]">
      <span
        className="px-1 h-[16px] inline-flex items-center rounded-sm shrink-0"
        style={
          run.kind === "worker"
            ? { color: "var(--status-running)", background: "var(--status-running-soft)" }
            : run.kind === "security"
              ? { color: "var(--text-secondary)", background: "var(--bg-inset)" }
              : { color: "var(--accent)", background: "var(--accent-soft)" }
        }
      >
        {run.kind}
      </span>
      <span className="text-text-tertiary shrink-0">
        {run.acted}/{run.examined}
      </span>
      <span
        className={cn("truncate", run.error ? "text-[color:var(--status-error)]" : "text-text-tertiary")}
        title={run.error ?? summarizeActions(run.actions)}
      >
        {run.error ?? summarizeActions(run.actions)}
      </span>
      <span className="ml-auto text-text-tertiary shrink-0">
        {formatClock(run.started_at)}
      </span>
    </div>
  );
}

function summarizeActions(actions: Record<string, unknown>[]): string {
  if (!actions || actions.length === 0) return "—";
  const first = actions[0];
  if (typeof first.surface === "string") {
    const parts = actions.map((a) => `${a.surface} ${a.findings ?? 0}`);
    const fresh = actions.reduce((n, a) => n + (Number(a.new) || 0), 0);
    return parts.join(" · ") + (fresh ? ` · ${fresh} new` : "");
  }
  if (first.paused) return `paused: ${first.paused}`;
  if (first.skipped) return `skipped: ${first.skipped}`;
  const dispatched = actions.filter((a) => a.dispatched).length;
  const quarantined = actions.filter((a) => a.quarantined).length;
  const routed = actions.filter((a) => a.routing).length;
  const bits: string[] = [];
  if (dispatched) bits.push(`${dispatched} dispatched`);
  if (quarantined) bits.push(`${quarantined} quarantined`);
  if (routed) bits.push(`${routed} triaged`);
  return bits.join(", ") || `${actions.length} actions`;
}
